import React, { useState, useEffect, useCallback } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Landing from './components/Landing';
import Schedule from './components/Schedule';
import Profile from './components/Profile';
import VkLinkPage from './components/VkLinkPage';
import Payment from './components/Payment';
import Curatorship from './components/Curatorship';
import AppShell from './components/AppShell';
import OfflineScreen from './components/OfflineScreen';
import axios from 'axios';
import { apiUrl } from './utils/apiConfig';
import { preloadApp } from './utils/preloadApp';
import { getToken, getUser, setUser as persistUser, clearAuthData, refreshTokenIfNeeded } from './utils/auth';
import { clearSchedulePrefs } from './utils/schedulePrefs';
import StickmanRunner from './components/StickmanRunner';
import SeasonalIntro from './components/SeasonalIntro';
import { ThemeProvider } from './theme/ThemeContext';
import { applyAppearance, appearanceFromUser } from './theme/appearance';
import { isNativeApp } from './utils/platform';
import AppToast from './components/AppToast';
import './styles/App.css';
import './styles/SeasonalTheme.css';
import './styles/appearance-dark.css';
import './styles/NoticePlate.css';

const ThemeEffects = () => <SeasonalIntro />;

const App = () => {
  const [token, setToken] = useState(() => {
    const stored = getToken();
    return stored && stored !== 'undefined' ? stored : null;
  });
  const [user, setUser] = useState(() => getUser());
  const [bootReady, setBootReady] = useState(false);
  const [bootProgress, setBootProgress] = useState(0);
  const [bootLabel, setBootLabel] = useState('Загрузка приложения...');
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false
  );

  useEffect(() => {
    applyAppearance(appearanceFromUser(user));
  }, [user]);

  // /landing → /#/ (как кнопка с ЭО на корень расписания). Pathname-алиас не ломаем.
  // В Android лендинга нет — гостя сразу на авторизацию.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/landing') {
      const hash = window.location.hash || '#/';
      window.history.replaceState(null, '', `/${hash.startsWith('#') ? hash : `#${hash}`}`);
    }
    if (!isNativeApp()) return;
    const hashPath =
      (window.location.hash || '#/').replace(/^#/, '').split('?')[0].replace(/\/+$/, '') || '/';
    if (hashPath === '/' || hashPath === '/landing') {
      window.location.replace('#/login');
    }
  }, []);

  const probeConnection = useCallback(async () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setIsOffline(true);
      return false;
    }

    try {
      await axios.get(apiUrl('/openapi.json'), {
        timeout: 3500,
        headers: { 'Cache-Control': 'no-cache' },
        validateStatus: () => true,
      });
      setIsOffline(false);
      return true;
    } catch {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setIsOffline(true);
        return false;
      }
      setIsOffline(false);
      return true;
    }
  }, []);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => {
      setIsOffline(false);
      probeConnection();
    };

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, [probeConnection]);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      try {
        setBootLabel('Загрузка приложения...');
        await preloadApp({
          token,
          onProgress: (p) => {
            if (cancelled) return;
            setBootProgress(p);
            if (p < 0.7) setBootLabel('Загрузка приложения...');
            else setBootLabel('Почти готово...');
          },
        });

        if (token && !cancelled) {
          try {
            const storedUser = getUser();
            if (storedUser) {
              setUser(storedUser);
            }
          } catch {
            /* ignore */
          }
        }
      } catch (error) {
        console.warn('Boot preload failed:', error);
      } finally {
        if (!cancelled) {
          setBootProgress(1);
          setBootReady(true);
        }
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!bootReady || !token) return undefined;
    let cancelled = false;

    const refreshUser = async () => {
      const loadMe = async (bearer) =>
        axios.get(apiUrl('/users/me'), {
          headers: {
            Authorization: `Bearer ${bearer}`,
            'Content-Type': 'application/json',
          },
          timeout: 4000,
        });

      try {
        await refreshTokenIfNeeded();
        if (cancelled) return;
        const current = getToken() || token;
        if (current && current !== token) {
          setToken(current);
        }
        const response = await loadMe(current);
        if (!cancelled) {
          setUser(response.data);
          persistUser(response.data);
        }
      } catch (error) {
        if (cancelled || error.response?.status !== 401) return;
        try {
          const ok = await refreshTokenIfNeeded();
          const current = getToken();
          if (ok && current) {
            const retry = await loadMe(current);
            if (!cancelled) {
              setToken(current);
              setUser(retry.data);
              persistUser(retry.data);
            }
            return;
          }
        } catch {
          /* fall through */
        }
        if (!cancelled) {
          clearSchedulePrefs();
          clearAuthData();
          setToken(null);
          setUser(null);
        }
      }
    };

    refreshUser();
    const keepAlive = window.setInterval(() => {
      refreshTokenIfNeeded().then((ok) => {
        const current = getToken();
        if (ok && current && current !== token) {
          setToken(current);
        }
      });
    }, 6 * 60 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(keepAlive);
    };
  }, [bootReady, token]);

  useEffect(() => {
    const handleStorageChange = () => {
      const storedUser = getUser();
      if (storedUser) {
        setUser(storedUser);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLogout = () => {
    try {
      sessionStorage.setItem('kkt_skip_autologin', '1');
    } catch {
      /* ignore */
    }
    // Сбрасываем быстрые настройки расписания до очистки auth,
    // чтобы после следующего входа снова показалось стартовое окно.
    clearSchedulePrefs();
    clearAuthData();
    setToken(null);
    setUser(null);
  };

  const handleUserUpdate = useCallback((next) => {
    setUser(next);
    persistUser(next);
  }, []);

  const nativeApp = isNativeApp();
  const pathLanding =
    typeof window !== 'undefined' &&
    (window.location.pathname.replace(/\/+$/, '') === '/landing' ||
      window.location.hash.replace(/^#/, '').replace(/\/+$/, '') === '/landing');

  // Гость на корне (#/) — лендинг в вебе; в Android сразу авторизация.
  if (pathLanding && !token && !nativeApp) {
    return <Landing />;
  }

  if (!bootReady) {
    const pct = Math.round(bootProgress * 100);
    const publicUrl = process.env.PUBLIC_URL || '';
    return (
      <>
        <OfflineScreen open={isOffline} onRetry={probeConnection} />
        <div className="app-boot" aria-busy="true">
          <div className="app-boot__bg" aria-hidden="true">
            <div className="app-boot__blob app-boot__blob--1" />
            <div className="app-boot__blob app-boot__blob--2" />
            <img
              className="app-boot__iso app-boot__iso--cube"
              src={`${publicUrl}/iso-cube-clear.png`}
              alt=""
              width={168}
              height={168}
              decoding="async"
            />
            <img
              className="app-boot__iso app-boot__iso--pyramid"
              src={`${publicUrl}/iso-pyramid-clear.png`}
              alt=""
              width={132}
              height={132}
              decoding="async"
            />
            <img
              className="app-boot__iso app-boot__iso--cube-sm"
              src={`${publicUrl}/iso-cube-clear.png`}
              alt=""
              width={92}
              height={92}
              decoding="async"
            />
          </div>
          <div className="app-boot__content">
            <img
              className="app-boot__logo"
              src={`${publicUrl}/logo.png`}
              alt="Логотип"
              width={72}
              height={72}
              decoding="sync"
              fetchPriority="high"
            />
            <StickmanRunner size="md" />
            <p>{bootLabel}</p>
            <div className="app-boot__bar" aria-hidden="true">
              <div className="app-boot__bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="app-boot__pct">{pct}%</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <ThemeProvider>
      <ThemeEffects />
      <HashRouter>
        <OfflineScreen open={isOffline} onRetry={probeConnection} />
        <Routes>
          <Route
            path="/"
            element={
              token ? (
                <Navigate to="/schedule" replace />
              ) : nativeApp ? (
                <Navigate to="/login" replace />
              ) : (
                <Landing />
              )
            }
          />
          <Route
            path="/landing"
            element={nativeApp ? <Navigate to="/login" replace /> : <Landing />}
          />
          <Route
            path="/login"
            element={
              token ? (
                <Navigate to="/schedule" replace />
              ) : (
                <Login setToken={setToken} setUser={setUser} />
              )
            }
          />
          <Route
            element={
              token ? (
                <AppShell user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          >
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/vk" element={<VkLinkPage />} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/curatorship" element={<Curatorship />} />
            <Route path="/curatorship/:groupId" element={<Curatorship />} />
          </Route>
        </Routes>
        <AppToast />
      </HashRouter>
    </ThemeProvider>
  );
};

export default App;
